import React, { PropsWithChildren, useState } from "react";
import { Icon } from "@atoms";
import { getFloorById, useAppSelector } from "@lib/store";
import { IconButton } from "@molecules";
import { useHereAndNow } from "@pages";
import { cn } from "@utils";

import { useTranslation } from "react-i18next";
import { trackEvent } from "@lib/infrastructure";

export const FloorSuggestionsTile = ({
  floorId,
  direction,
  children,
  onClick,
}: PropsWithChildren<{ floorId: string | null; direction: "down" | "up"; onClick?: () => void }>) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const { tab, buildingId, usageStatsPerFloor, setSelectedFloorId, setSelectedObject } = useHereAndNow();

  const usageStats = floorId && usageStatsPerFloor.get(floorId);

  const floor = useAppSelector(state => (floorId ? getFloorById(state, floorId) : null));

  if (!floor) return null;

  const handeFloorClick = () => {
    onClick?.();
    setSelectedFloorId(floor.id);
    setSelectedObject(null);

    trackEvent("HereAndNow_OtherFloorSuggestions__FloorClick", {
      buildingId,
      tab,
      floorId: floorId || "",
      type: direction === "up" ? "above" : "below",
    });
  };

  return (
    <>
      <div
        className="p-2 flex gap-4 items-center w-full cursor-pointer rounded-lg hover:bg-mapiq-black-50"
        onClick={handeFloorClick}>
        <div className="group flex items-center flex-grow border-red-500 gap-3">
          <div className="size-12 rounded-lg bg-beige-500* flex-none flex items-center justify-center text-black">
            <Icon
              icon={direction === "down" ? "arrowDown" : "arrowUp"}
              className={cn(
                "size-4 transition-transform",
                direction === "up" ? "group-hover:-translate-y-1" : "group-hover:translate-y-1",
              )}
            />
            <Icon
              icon="floor"
              className="size-4"
            />
          </div>

          <div className="flex-grow flex flex-col items-start">
            <h4 className="">{`${t("Floor")} ${floor.number}`}</h4>
            {usageStats && (
              <>
                <p className="text-mapiq-black-500">
                  {tab === "rooms"
                    ? t("hereAndNow:AvailableRoom", { count: usageStats.available })
                    : t("hereAndNow:AvailableWorkspace", { count: usageStats.available })}
                </p>

                <div className="flex w-full max-w-40">
                  <span
                    className="block bg-functional-green h-2  transition-[width] rounded-sm transform origin-left"
                    style={{
                      width: `${usageStats.ratio * 100}%`,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {!!children && (
          <IconButton
            icon="caretDown"
            size="small"
            className={cn("[&>svg]:transition-transform", !expanded ? "[&>svg]:rotate-0" : "[&>svg]:rotate-180")}
            onClick={e => {
              e.stopPropagation();
              setExpanded(prev => {
                const next = !prev;

                if (next) {
                  trackEvent("HereAndNow_OtherFloorSuggestions__Expand", {
                    buildingId,
                    tab,
                    floorId: floorId || "",
                    type: direction === "up" ? "above" : "below",
                  });
                } else {
                  trackEvent("HereAndNow_OtherFloorSuggestions__Collapse", {
                    buildingId,
                    tab,
                    floorId: floorId || "",
                    type: direction === "up" ? "above" : "below",
                  });
                }
                return next;
              });
            }}
            iconButton={"tertiary"}
          />
        )}
      </div>

      {expanded && children}
    </>
  );
};
